import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { serverApi, stationApi } from '../services/api';

interface Server {
  id: number;
  name: string;
  url: string;
}

interface Station {
  id: number;
  name: string;
  program: string;
  mac: string;
  gas0Tag: string;
  gas1Tag: string;
}

const ProductionManagementPage: React.FC = () => {
  const [servers, setServers] = useState<Server[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [selectedServer, setSelectedServer] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // 對話框狀態
  const [serverDialogOpen, setServerDialogOpen] = useState(false);
  const [stationDialogOpen, setStationDialogOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<Server | null>(null);
  const [editingStation, setEditingStation] = useState<Station | null>(null);
  
  // 表單狀態
  const [serverForm, setServerForm] = useState({ name: '', url: '' });
  const [stationForm, setStationForm] = useState({ 
    name: '', 
    program: '', 
    mac: '', 
    gas0Tag: '', 
    gas1Tag: '' 
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [serversResponse, stationsResponse] = await Promise.all([
        serverApi.getAll(),
        stationApi.getAll()
      ]);
      setServers(serversResponse.data);
      setStations(stationsResponse.data);
    } catch (err) {
      setError('載入資料失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleServerSubmit = async () => {
    try {
      if (editingServer) {
        await serverApi.update(editingServer.id, serverForm);
        setSuccess('伺服器更新成功');
      } else {
        await serverApi.create(serverForm);
        setSuccess('伺服器新增成功');
      }
      setServerDialogOpen(false);
      setEditingServer(null);
      setServerForm({ name: '', url: '' });
      loadData();
    } catch (err) {
      setError('操作失敗');
    }
  };

  const handleStationSubmit = async () => {
    try {
      if (editingStation) {
        await stationApi.update(editingStation.id, stationForm);
        setSuccess('工作站更新成功');
      } else {
        await stationApi.create(stationForm);
        setSuccess('工作站新增成功');
      }
      setStationDialogOpen(false);
      setEditingStation(null);
      setStationForm({ name: '', program: '', mac: '', gas0Tag: '', gas1Tag: '' });
      loadData();
    } catch (err) {
      setError('操作失敗');
    }
  };

  const handleEditServer = (server: Server) => {
    setEditingServer(server);
    setServerForm({ name: server.name, url: server.url });
    setServerDialogOpen(true);
  };

  const handleEditStation = (station: Station) => {
    setEditingStation(station);
    setStationForm({
      name: station.name,
      program: station.program,
      mac: station.mac,
      gas0Tag: station.gas0Tag,
      gas1Tag: station.gas1Tag
    });
    setStationDialogOpen(true);
  };

  const handleDeleteServer = async (id: number) => {
    if (window.confirm('確定要刪除此伺服器嗎？')) {
      try {
        await serverApi.delete(id);
        setSuccess('伺服器刪除成功');
        loadData();
      } catch (err) {
        setError('刪除失敗');
      }
    }
  };

  const handleDeleteStation = async (id: number) => {
    if (window.confirm('確定要刪除此工作站嗎？')) {
      try {
        await stationApi.delete(id);
        setSuccess('工作站刪除成功');
        loadData();
      } catch (err) {
        setError('刪除失敗');
      }
    }
  };

  const filteredStations = selectedServer 
    ? stations.filter(station => station.id === selectedServer)
    : stations;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        產線管理
      </Typography>

      {/* 伺服器管理 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">伺服器管理</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingServer(null);
                setServerForm({ name: '', url: '' });
                setServerDialogOpen(true);
              }}
            >
              新增伺服器
            </Button>
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>名稱</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servers.map((server) => (
                  <TableRow key={server.id}>
                    <TableCell>{server.name}</TableCell>
                    <TableCell>{server.url}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditServer(server)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteServer(server.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 工作站管理 */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">工作站管理</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>選擇伺服器</InputLabel>
                <Select
                  value={selectedServer}
                  onChange={(e) => setSelectedServer(e.target.value as number | '')}
                >
                  <MenuItem value="">全部</MenuItem>
                  {servers.map((server) => (
                    <MenuItem key={server.id} value={server.id}>
                      {server.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingStation(null);
                  setStationForm({ name: '', program: '', mac: '', gas0Tag: '', gas1Tag: '' });
                  setStationDialogOpen(true);
                }}
              >
                新增工作站
              </Button>
            </Box>
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>名稱</TableCell>
                  <TableCell>程式</TableCell>
                  <TableCell>MAC</TableCell>
                  <TableCell>Gas0 Tag</TableCell>
                  <TableCell>Gas1 Tag</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStations.map((station) => (
                  <TableRow key={station.id}>
                    <TableCell>{station.name}</TableCell>
                    <TableCell>{station.program}</TableCell>
                    <TableCell>{station.mac}</TableCell>
                    <TableCell>{station.gas0Tag}</TableCell>
                    <TableCell>{station.gas1Tag}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditStation(station)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteStation(station.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 伺服器對話框 */}
      <Dialog open={serverDialogOpen} onClose={() => setServerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingServer ? '編輯伺服器' : '新增伺服器'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="名稱"
            fullWidth
            variant="outlined"
            value={serverForm.name}
            onChange={(e) => setServerForm({ ...serverForm, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            value={serverForm.url}
            onChange={(e) => setServerForm({ ...serverForm, url: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServerDialogOpen(false)}>取消</Button>
          <Button onClick={handleServerSubmit} variant="contained">確定</Button>
        </DialogActions>
      </Dialog>

      {/* 工作站對話框 */}
      <Dialog open={stationDialogOpen} onClose={() => setStationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingStation ? '編輯工作站' : '新增工作站'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="名稱"
            fullWidth
            variant="outlined"
            value={stationForm.name}
            onChange={(e) => setStationForm({ ...stationForm, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="程式"
            fullWidth
            variant="outlined"
            value={stationForm.program}
            onChange={(e) => setStationForm({ ...stationForm, program: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="MAC"
            fullWidth
            variant="outlined"
            value={stationForm.mac}
            onChange={(e) => setStationForm({ ...stationForm, mac: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Gas0 Tag"
            fullWidth
            variant="outlined"
            value={stationForm.gas0Tag}
            onChange={(e) => setStationForm({ ...stationForm, gas0Tag: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Gas1 Tag"
            fullWidth
            variant="outlined"
            value={stationForm.gas1Tag}
            onChange={(e) => setStationForm({ ...stationForm, gas1Tag: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStationDialogOpen(false)}>取消</Button>
          <Button onClick={handleStationSubmit} variant="contained">確定</Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductionManagementPage;
